/**
 * Landing-page odds and ends: the share affordances on the "no files yet" lane,
 * and the feature-flagged reveal of the import demo clip.
 *
 * Deliberately standalone — it touches nothing the installer or the engine owns,
 * imports nothing, and is safe to fail. A landing page that cannot copy a URL to
 * the clipboard is still a landing page.
 */

const SITE = 'https://rerunreactor.com';

/** Minimal local toast. The shell has a richer one; this file stays dependency-free. */
function say(message: string): void {
  const host = document.getElementById('toasts');
  if (!host) return;
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  host.appendChild(el);
  setTimeout(() => {
    el.style.transition = 'opacity .25s';
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 260);
  }, 2400);
}

/**
 * "Send this desk to yourself" is a mailto and nothing else: no form, no
 * endpoint, no address of ours anywhere in it. The recipient is left blank so
 * the mail client opens on the To: field, which is where the user's own address
 * goes.
 */
function wireSendToSelf(): void {
  const link = document.querySelector<HTMLAnchorElement>('#link-send-self');
  if (!link) return;
  const subject = 'Fallout 2, in a browser tab';
  const body = [
    `The desk: ${SITE}`,
    '',
    'Bring your own copy of Fallout 2 — GOG or Steam, $9.99. The files are read',
    'on your machine and uploaded nowhere. About ten minutes at the right computer.',
  ].join('\n');
  link.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function wireCopyLink(): void {
  const btn = document.querySelector<HTMLButtonElement>('#btn-copy-link');
  if (!btn) return;
  btn.addEventListener('click', () => {
    void navigator.clipboard
      ?.writeText(SITE)
      .then(() => say('Link copied.'))
      .catch(() => say(SITE));
  });
}

/**
 * The demo clip is filmed on its own schedule, so the markup ships hidden and is
 * revealed only when the poster is genuinely fetchable. Deploying ahead of the
 * video renders a clean page rather than a broken frame.
 *
 * Playback starts on intersection rather than on load: an autoplaying loop that
 * nobody has scrolled to is bandwidth spent on an empty room. Reduced-motion
 * users get the poster and a control bar instead of a loop.
 */
function wireDemoSlot(): void {
  const slot = document.querySelector<HTMLElement>('#demo-slot');
  const video = document.querySelector<HTMLVideoElement>('#demo-video');
  const poster = slot?.dataset.demoPoster;
  if (!slot || !video || !poster) return;

  // A bare `res.ok` is not enough: both the dev server and any host with an
  // SPA fallback answer a missing /shots/*.jpg with 200 and the index document,
  // which would reveal an empty black frame. The content type is what actually
  // distinguishes "the clip has landed" from "the clip has not".
  void fetch(poster, { method: 'HEAD' })
    .then((res) => {
      if (!res.ok) return;
      if (!(res.headers.get('content-type') ?? '').startsWith('image/')) return;
      slot.hidden = false;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        video.controls = true;
        return;
      }
      video.preload = 'metadata';
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) void video.play().catch(() => undefined);
          else video.pause();
        }
      });
      io.observe(video);
    })
    .catch(() => undefined);
}

wireSendToSelf();
wireCopyLink();
wireDemoSlot();
